import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Check, ChevronDown, FlaskConical, HelpCircle, LogOut, Menu, Plus, Search, Settings as SettingsIcon, ShieldCheck, UserRound } from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace, useWsQuery } from "@/context/WorkspaceContext";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { Field } from "@/components/common";

const menuCls = "bg-[#0A1024] border-[#243153] text-[#F7FAFF]";

function NewWorkspaceDialog({ open, onOpenChange }) {
  const { switchTo, reloadWorkspaces } = useWorkspace();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const create = async (demo) => {
    setBusy(true);
    try {
      const { data } = demo ? await api.post("/workspaces/demo") : await api.post("/workspaces", { name });
      await reloadWorkspaces(); switchTo(data.id); onOpenChange(false); setName("");
      toast.success(`Workspace “${data.name}” created`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-workspace-dialog" className={menuCls}>
        <DialogHeader><DialogTitle className="font-display">New workspace</DialogTitle></DialogHeader>
        <p className="text-sm text-[#AAB6CC]">A workspace represents one company or brand. Data never crosses workspaces.</p>
        <Field label="Company or brand name"><input data-testid="new-workspace-name-input" className="cq-input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <div className="flex flex-col sm:flex-row gap-2 justify-between">
          <button data-testid="load-demo-workspace-button" disabled={busy} onClick={() => create(true)} className="cq-btn cq-btn-ghost"><FlaskConical size={14} /> Load AccruallQ demo</button>
          <button data-testid="create-workspace-button" disabled={busy || !name.trim()} onClick={() => create(false)} className="cq-btn cq-btn-primary">Create workspace</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WorkspaceSwitcher() {
  const { workspaces, current, switchTo } = useWorkspace();
  const [open, setOpen] = useState(false);
  return (<>
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="workspace-selector" className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg border border-[#243153] hover:border-[#34457A] min-w-0 max-w-[132px] sm:max-w-[220px]">
        <span className="w-6 h-6 rounded-md flex items-center justify-center font-display text-xs font-semibold text-[#050816] shrink-0" style={{ background: "var(--cq-gradient)" }}>{current?.name?.[0]}</span>
        <span className="text-sm font-medium text-[#F7FAFF] truncate">{current?.name}</span>
        <ChevronDown size={14} className="text-[#7C8BAD] shrink-0" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className={`${menuCls} w-64`}>
        <DropdownMenuLabel className="cq-muted-label">Workspaces</DropdownMenuLabel>
        {workspaces.map((w) => (
          <DropdownMenuItem key={w.id} data-testid={`workspace-option-${w.slug}`} onClick={() => switchTo(w.id)} className="flex items-center gap-2 focus:bg-[#131F42] focus:text-[#F7FAFF]">
            <span className="flex-1 truncate">{w.name}</span>
            {w.is_demo && <span className="font-mono text-[9.5px] text-[#F4B860]">DEMO</span>}
            {w.id === current?.id && <Check size={14} className="text-[#22E6F2]" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator className="bg-[#243153]" />
        <DropdownMenuItem data-testid="new-workspace-menu-item" onClick={() => setOpen(true)} className="focus:bg-[#131F42] focus:text-[#F7FAFF]"><Plus size={14} className="mr-2" /> New workspace</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    <NewWorkspaceDialog open={open} onOpenChange={setOpen} />
  </>);
}

function SearchBox() {
  const { wid, path } = useWorkspace();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [deb, setDeb] = useState("");
  const ref = useRef(null);
  useEffect(() => { const t = setTimeout(() => setDeb(q), 250); return () => clearTimeout(t); }, [q]);
  useEffect(() => {
    const k = (e) => { if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); ref.current?.focus(); } };
    window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k);
  }, []);
  const res = useQuery({ queryKey: ["ws", wid, "search", deb], queryFn: () => api.get(path(`/search?q=${encodeURIComponent(deb)}`)).then((r) => r.data), enabled: deb.length >= 2 });
  const go = (link) => { setQ(""); nav(link); };
  return (
    <div className="relative hidden md:block w-full max-w-xs">
      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7C8BAD]" />
      <input ref={ref} data-testid="global-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search campaigns, content…" className="cq-input !py-2 !pl-9 !pr-12 !text-[13px]" />
      <kbd className="absolute right-3 top-1/2 -translate-y-1/2 font-mono text-[10px] text-[#5D6B8C]">⌘K</kbd>
      {deb.length >= 2 && q && (
        <div data-testid="search-results" className="absolute top-full mt-2 w-full rounded-xl border border-[#243153] bg-[#0A1024] shadow-2xl z-50 py-2 max-h-80 overflow-y-auto">
          {res.isLoading ? <div className="px-4 py-3 text-sm text-[#AAB6CC]">Searching…</div> : (res.data || []).length === 0 ? <div className="px-4 py-3 text-sm text-[#AAB6CC]">No matches</div> :
            res.data.map((r, i) => (
              <button key={i} data-testid={`search-result-${i}`} onClick={() => go(r.link)} className="w-full text-left px-4 py-2 hover:bg-[#131F42] flex items-center gap-3">
                <span className="cq-muted-label w-16 shrink-0">{r.type}</span><span className="text-sm text-[#F7FAFF] truncate">{r.title}</span>
              </button>))}
        </div>
      )}
    </div>
  );
}

function Notifications() {
  const { path, wid } = useWorkspace();
  const qc = useQueryClient();
  const nav = useNavigate();
  const q = useWsQuery("/notifications", { refetchInterval: 60000 });
  const readAll = async () => { await api.post(path("/notifications/read-all")); qc.invalidateQueries({ queryKey: ["ws", wid, "/notifications"] }); };
  const unread = q.data?.unread || 0;
  return (
    <Popover>
      <PopoverTrigger data-testid="notifications-button" className="relative w-9 h-9 shrink-0 rounded-lg flex items-center justify-center text-[#AAB6CC] hover:text-[#F7FAFF] hover:bg-[#0E1733]">
        <Bell size={17} />
        {unread > 0 && <span data-testid="notifications-unread-count" className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#22E6F2] text-[#050816] text-[10px] font-bold flex items-center justify-center">{unread}</span>}
      </PopoverTrigger>
      <PopoverContent align="end" className={`${menuCls} w-80 p-0`} data-testid="notifications-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#243153]">
          <span className="font-display text-sm font-medium">Notifications</span>
          <button data-testid="notifications-mark-read" onClick={readAll} className="text-xs text-[#22E6F2] hover:underline">Mark all read</button>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {(q.data?.items || []).length === 0 ? <p className="p-4 text-sm text-[#AAB6CC]">You're all caught up.</p> :
            q.data.items.map((n) => (
              <button key={n.id} onClick={() => n.link && nav(n.link)} className="w-full text-left px-4 py-3 border-b border-[#172342] hover:bg-[#131F42]">
                <div className="flex items-center gap-2">
                  {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#22E6F2]" />}
                  <span className="text-sm text-[#F7FAFF]">{n.title}</span>
                  {n.is_demo && <span className="font-mono text-[9px] text-[#F4B860]">DEMO</span>}
                </div>
                <div className="text-xs text-[#AAB6CC] mt-0.5">{n.body}</div>
                <div className="text-[10.5px] font-mono text-[#5D6B8C] mt-1">{fmtDateTime(n.created_at)}</div>
              </button>))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Help() {
  return (
    <Popover>
      <PopoverTrigger data-testid="help-button" className="hidden sm:flex w-9 h-9 shrink-0 rounded-lg items-center justify-center text-[#AAB6CC] hover:text-[#F7FAFF] hover:bg-[#0E1733]"><HelpCircle size={17} /></PopoverTrigger>
      <PopoverContent align="end" className={`${menuCls} w-80`} data-testid="help-panel">
        <div className="cq-label mb-2">How CRESQEN works</div>
        <p className="text-sm text-[#AAB6CC] leading-relaxed">Type an outcome in the Command Centre. CRESQEN interprets it, you edit and confirm, then it drafts a strategy for your approval.</p>
        <div className="mt-4 p-3 rounded-lg bg-[#050816] border border-[#243153] flex gap-2.5 text-xs text-[#AAB6CC]">
          <ShieldCheck size={15} className="text-[#4BE3A4] shrink-0" /> Human in control: nothing is published, launched or paid for in this build. Every consequential decision is logged.
        </div>
        <div className="mt-4 text-xs text-[#7C8BAD] space-y-1">
          <div>⌘K — search</div><div>Low-confidence findings are informational only.</div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Profile() {
  const { user, logout } = useAuth();
  const { can } = useWorkspace();
  const nav = useNavigate();
  const item = "focus:bg-[#131F42] focus:text-[#F7FAFF] cursor-pointer";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="user-profile-button" aria-label="Open account menu"
        className="shrink-0 flex items-center gap-2 pl-1 pr-1.5 py-1 rounded-full border border-[#243153] hover:border-[#34457A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#22E6F2]">
        {user?.picture ? <img src={user.picture} alt="" className="w-7 h-7 rounded-full" /> :
          <span className="w-7 h-7 rounded-full bg-[#131F42] flex items-center justify-center text-xs font-semibold text-[#F7FAFF]">{(user?.name || "?").slice(0, 1).toUpperCase()}</span>}
        <span className="hidden xl:block text-sm text-[#F7FAFF] max-w-[120px] truncate">{user?.name}</span>
        <ChevronDown size={14} className="text-[#7C8BAD]" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={`${menuCls} w-64`} data-testid="user-menu">
        <DropdownMenuLabel><div className="text-sm truncate">{user?.name}</div><div className="text-xs text-[#7C8BAD] font-normal truncate">{user?.email}</div></DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-[#243153]" />
        <DropdownMenuItem data-testid="menu-my-profile" onSelect={() => nav("/account?tab=profile")} className={item}><UserRound size={14} className="mr-2" /> My Profile</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-account-security" onSelect={() => nav("/account?tab=security")} className={item}><ShieldCheck size={14} className="mr-2" /> Account Security</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-notification-settings" onSelect={() => nav("/account?tab=notifications")} className={item}><Bell size={14} className="mr-2" /> Notification Settings</DropdownMenuItem>
        {can("manage_workspace") && <DropdownMenuItem data-testid="menu-workspace-settings" onSelect={() => nav("/settings")} className={item}><SettingsIcon size={14} className="mr-2" /> Workspace Settings</DropdownMenuItem>}
        <DropdownMenuSeparator className="bg-[#243153]" />
        <DropdownMenuItem data-testid="logout-button" onSelect={logout} className="focus:bg-[#131F42] focus:text-[#FF7792] text-[#FF7792] cursor-pointer"><LogOut size={14} className="mr-2" /> Sign Out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Topbar({ onOpenMenu }) {
  const { can } = useWorkspace();
  return (
    <header data-testid="topbar" className="sticky top-0 z-40 h-16 cq-glass border-b border-[#243153] flex items-center gap-2 sm:gap-3 px-3 sm:px-6 min-w-0">
      <button data-testid="mobile-menu-button" onClick={onOpenMenu} className="lg:hidden w-9 h-9 shrink-0 rounded-lg flex items-center justify-center text-[#AAB6CC] hover:bg-[#0E1733]"><Menu size={18} /></button>
      <WorkspaceSwitcher />
      <div className="flex-1 min-w-0 flex justify-center"><SearchBox /></div>
      <Notifications />
      <Help />
      {can("edit") && <Link to="/command-centre?new=1" data-testid="new-campaign-button" className="cq-btn cq-btn-primary shrink-0 !px-2.5 sm:!px-4"><Plus size={15} /><span className="hidden sm:inline">New Campaign</span></Link>}
      <Profile />
    </header>
  );
}
